<script lang="ts">
  import { slide } from 'svelte/transition';
  import * as Dialog from '$lib/components/ui/dialog';
  import Button from '@/lib/components/ui/button/button.svelte';
  import SuitabilityBadges from './SuitabilityBadges.svelte';
  import { kmToMile, convertSeconds } from '@/Util/utils';
  import { format_duration } from '@/time';
  import { getActivityRecordsData, getWorkoutData } from '@/Util/api';
  import { Activity, type Exercise } from '@/Common/ActivityFunctions';
  import TrainingCardTrainingValues from './TrainingCardTrainingValues.svelte';
  import { getContext, tick } from 'svelte';
  import type { MyFitnessStores } from '../stores';
  import Am5Loader from '@/Util/Am5Loader.svelte';
  import ActivityChartNew from '@/Activities/ActivityChartNew.svelte';
  import ActivityMap from '@/Activities/ActivityMap.svelte';
  import { processRecordsForChart, type ActivityChartMpaDataPromise } from '@/Util/XertAm5';
  import type { Polyline, SeriesVisibility, TodaysActivities } from '@/Common/constants';
  import ExerciseDialogActions from './ExerciseDialogActions.svelte';
  import ActivityCardTitleDetails from '@/Activities/Dashboard/ActivityCardTitleDetails.svelte';
  import ActivityCardDetails from '@/Activities/Dashboard/ActivityCardDetails.svelte';
  import type { CalendarLoader } from '../Calendar/CalendarLoaderNew';
  import CommentThreadPanel from '@/Comments/CommentThreadPanel.svelte';
  import { getActivityComments, postActivityComment, markActivityCommentsRead } from '@/Comments/api';

  export let open: boolean;
  export let exercise: Exercise;
  export let loading: boolean = false; // optional external loading state
  export let calendarLoader: CalendarLoader | null = null;
  export let todaysActivities: TodaysActivities | null = null;
  export let rootDiv: HTMLElement | null = null;

  export let hideScheduleButtons: boolean = false;
  
  const myFitness = getContext<MyFitnessStores>('myFitness')
  const { userParams, trainingAdvice } = myFitness

  //@ts-ignore
  const Xert = window.Xert;
  
  // Derived
  let useFreedomUnits: boolean = Xert?._displayUnits === 'imperial';
  $: distanceVal = exercise?.distance ? (useFreedomUnits ? kmToMile(exercise.distance) : exercise.distance) : 0;
  $: distanceUnit = useFreedomUnits ? 'mi' : 'km';
  $: durationStr = convertSeconds(exercise?.duration ?? exercise?.adu, false);
  $: suitabilityList = exercise?.suitability?.split(',').map(s=>s.trim()).filter(Boolean) ?? [];
  $: detailPageLink = Activity.getDetailsPageLink(exercise);
  $: isActivity = Activity.isActivity(exercise);
  // A recorded ride has the numbers the dashboard card is built out of, so it shows the same body.
  // Workouts and anything still only planned keep the plain training-values list.
  $: isRecordedActivity = isActivity && Activity.isRecorded(exercise);

  $: chartSeriesVisibility = ((): SeriesVisibility => {
    if (Activity.isWorkout(exercise)) {
      // Workout: show power profile, hide sensor data (all zeros for workouts)
      return { mpa: 'startVisible', power: 'startVisible', difficulty: 'startVisible', heartRate: 'off', speed: 'off', targetPower: 'off', cadence: 'off', altitude: 'off' };
    }
    if (exercise?.hasPowerData && !Activity.isHeartrateDerived(exercise)) {
      // Recorded activity with real power data
      return { mpa: 'startVisible', power: 'startVisible',  difficulty: 'startVisible', heartRate: 'startHidden', speed: 'startHidden', targetPower: 'startHidden', cadence: 'startHidden', altitude: 'startHidden' };
    }
    // No real power (non-power activity or HR-derived): hide power-based series, show HR
    return { mpa: 'off', power: 'off', difficulty: 'startVisible', heartRate: 'startVisible', speed: 'startVisible', targetPower: 'off', cadence: 'startVisible', altitude: 'startVisible' };
  })();

  // The body is a scroll container of its own, and the dialog only puts it in the DOM once it
  // opens, so every open starts from wherever the last one was left -- and anything inside that
  // scrolls itself into view on load drags this along with it. Put it back at the top: the title
  // and the ride's own numbers are what the dialog is for, not whatever is last in it.
  let bodyEl: HTMLElement | null = null;
  $: if (open) void scrollBodyToTop();
  async function scrollBodyToTop() {
    await tick();
    if (bodyEl) bodyEl.scrollTop = 0;
  }

  let activeTab: 'chart' | 'map' | null = 'chart';
  let mapPolyline: Polyline | null = null;

  let chartData: Awaited<ActivityChartMpaDataPromise>
  // Workout points know the length of the interval they belong to, recorded rides don't.
  let chartHasIntervalDurations = false
  let movingAverage = 5
  // This intermediate variable is used to reload the chart if the exercise changes,
  // but to not react every single time that reactivity is triggered on exercise.
  // Otherwise, the chart reloads repeatedly for any calendar update.
  let exercisePathForChart: string = null;
  async function getChartData() {
    if (!exercise) return;
    if (Activity.isActivity(exercise) && Activity.isRecorded(exercise)) {
      const data = await getActivityRecordsData(exercise.path);
      if (!data) return;
      mapPolyline = data.polyline?.length ? data.polyline : null;
      chartHasIntervalDurations = false;
      chartData = await processRecordsForChart(data, data.sig, $userParams.metric, movingAverage);
    } else if ((Activity.isWorkout(exercise) || Activity.getWorkoutPath(exercise)) && (exercise.signature || $trainingAdvice?.signature)) {
      const response = await getWorkoutData(Activity.isWorkout(exercise) ? exercise.path : Activity.getWorkoutPath(exercise));
      if (!response?.data?.length) return;
      const pts = response.data;
      const data = {
        time: pts.map(d => d.seconds * 1000),
        power: pts.map(d => d.power),
        tgt: pts.map(d => d.power),
        hr: pts.map(() => 0),
        cad: pts.map(() => 0),
        spd: pts.map(() => 0),
        dist: pts.map(() => 0),
        lat: pts.map(() => 0),
        lng: pts.map(() => 0),
        alt: pts.map(() => 0),
        polyline: [] as [number, number][],
      };
      const sig = { ...(exercise.signature ?? $trainingAdvice?.signature), carb_bias: 0 };
      const processed = await processRecordsForChart(data, sig, $userParams.metric, movingAverage);
      // Carry each interval's length onto the chart points, so hovering the power also says how
      // long that power is held for. Chart points keep the workout point's time in `timestamp`.
      const intervalDurations = new Map(pts.map(d => [d.seconds, d.interval_duration]));
      for (const point of processed.data) {
        const duration = intervalDurations.get(Math.round(point.timestamp));
        if (duration && duration > 0) point.interval_duration_str = format_duration(duration);
      }
      chartHasIntervalDurations = true;
      chartData = processed;
    }
  }
  $: {
    if (exercise && exercise?.path != exercisePathForChart) {
      chartData = undefined;
      mapPolyline = null;
      activeTab = 'chart';
      exercisePathForChart = exercise.path;
    }
  };
  $: {
    if (exercise && open && chartData == undefined) {
      getChartData()
    }
  }

</script>

<Dialog.Root bind:open={open} portal="#shadcn-ui-dialog-target">
  <Dialog.Content class="flex flex-row gap-0 min-h-[min(600px,95dvh)] max-h-[95dvh] max-w-[1000px] w-[95vw] p-0 overflow-hidden rounded-xl">
    <!-- Left preview panel -->
    <div class="hidden lg:block w-[320px] space-y-4 shrink-0 border-r border-border p-4 bg-muted/30 overflow-y-auto overflow-x-hidden">
      <ExerciseDialogActions bind:exercise {loading} {hideScheduleButtons} bind:open {calendarLoader} {todaysActivities} {rootDiv} />
    </div>

    <!-- Right detail/action panel -->
    <div class="flex-1 flex flex-col min-w-0">
      <div class='w-full flex-1 overflow-hidden flex flex-row gap-0'>
        <!-- Left preview panel -->
        <div class="hidden md:block lg:hidden w-[320px] space-y-4 shrink-0 border-r border-border p-4 bg-muted/30 overflow-y-auto overflow-x-hidden">
          <ExerciseDialogActions bind:exercise {loading} {hideScheduleButtons} bind:open {calendarLoader} {todaysActivities} {rootDiv} />
        </div>
        <div class="flex-1 flex flex-col min-w-0">
          <!-- Header -->
          <div class="flex items-start gap-4 px-8 pt-4 pb-3 border-b border-border">
            <div class="flex-1 min-w-0">
              <h2 class="text-xl font-semibold leading-tight truncate" title={exercise?.name}>
                {#if detailPageLink}
                  <a class='text-foreground hover:underline' href={detailPageLink} target={Activity.isWorkout(exercise) ? "workout" : "activity"}>
                    {exercise.name}
                  </a>
                {:else}
                  {exercise.name}
                {/if}
              </h2>
              <div class="mt-1 flex flex-wrap gap-2 items-center text-xs text-muted-foreground">
                {#if durationStr}<span class="inline-flex items-center gap-1"><i class="fa-regular fa-clock text-[10px]"></i>{durationStr}</span>{/if}
                {#if distanceVal > 0}<span class="inline-flex items-center gap-1"><i class="fa-regular fa-road text-[10px]"></i>{distanceVal.toFixed(1)} {distanceUnit}</span>{/if}
              </div>
            </div>
          </div>
    
          <div bind:this={bodyEl} class="flex-1 overflow-y-auto pl-8 pr-5 pb-5 pt-4 space-y-6">
            <!-- Suitability & Focus -->
            {#if suitabilityList.length > 0}
              <section class="space-y-2">
                <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Suitability</h3>
                <SuitabilityBadges suitability={suitabilityList} showFullText isLiked={exercise.liked || exercise.favorite} isDisliked={exercise.disliked} />
              </section>
            {/if}
    
            {#if isRecordedActivity}
              <!-- The same body as the long dashboard card: tags, group members, description,
                   gauges, metrics, fitness context and signature. -->
              <div class="flex flex-col gap-2.5">
                <ActivityCardTitleDetails {exercise} showHeader={false} />
                <ActivityCardDetails {exercise} />
              </div>
            {:else}
              <!-- Training Values -->
              <TrainingCardTrainingValues {exercise} />

              <!-- Description -->
              {#if !!exercise.description?.trim()}
              <section class="space-y-3">
                <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Description</h3>
                <p class="text-sm">{exercise.description}</p>
              </section>
              {/if}
            {/if}

            {#if isActivity && exercise?.path && Xert._hasMessagingFeature}
            {@const path = exercise.path}
            <section class="space-y-3">
              <h3 class="text-sm font-medium tracking-wide text-muted-foreground">Comments</h3>
              {#key path}
                <!-- Not scrolled to the newest comment on load: the thread sits at the bottom of a
                     long dialog body, and jumping to it takes the whole dialog with it. -->
                <CommentThreadPanel
                  scrollToLatestOnLoad={false}
                  load={(before) => getActivityComments(path, before)}
                  send={(text, files, references, replyToId) => postActivityComment(path, text, files, references, replyToId)}
                  markRead={() => markActivityCommentsRead(path)}
                  onRead={() => myFitness.markCommentsRead(path)}
                />
              {/key}
            </section>
            {/if}

            <div class='block md:hidden space-y-4'>
              <ExerciseDialogActions bind:exercise {loading} {hideScheduleButtons} bind:open {calendarLoader} {todaysActivities} {rootDiv} />
            </div>
          </div>
        </div>
      </div>

      {#if chartData}
        <div class="relative shrink-0" in:slide={{ duration: 400 }}>
          <div class="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1">
            <button
              class="h-7 px-3 rounded-full border border-border text-xs transition-colors flex items-center gap-1.5 {activeTab === 'chart' ? 'bg-accent text-foreground' : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-accent'}"
              on:click={() => activeTab = activeTab === 'chart' ? null : 'chart'}
            >
              <i class="fa-regular fa-chart-line text-[10px]"></i>
              <i class="fa-regular fa-chevron-{activeTab === 'chart' ? 'down' : 'up'} text-[10px]"></i>
            </button>
            {#if mapPolyline?.length}
              <button
                class="h-7 px-3 rounded-full border border-border text-xs transition-colors flex items-center gap-1.5 {activeTab === 'map' ? 'bg-accent text-foreground' : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-accent'}"
                on:click={() => activeTab = activeTab === 'map' ? null : 'map'}
              >
                <i class="fa-regular fa-map text-[10px]"></i>
                <i class="fa-regular fa-chevron-{activeTab === 'map' ? 'down' : 'up'} text-[10px]"></i>
              </button>
            {/if}
          </div>
          <div class="overflow-hidden border-t border-border transition-[max-height] duration-300 ease-in-out" style:max-height={activeTab === null ? '0px' : '600px'}>
            <div>
              <div class='px-5' class:hidden={activeTab !== 'chart'}>
                <Am5Loader>
                  <ActivityChartNew mpaData={chartData} hideYAxis showScrollbar={false} showLegend={false}
                    seriesVisibility={chartSeriesVisibility}
                    showIntervalDuration={chartHasIntervalDurations}
                  />
                </Am5Loader>
              </div>
              {#if mapPolyline?.length}
                <div class='px-5 py-8' class:hidden={activeTab !== 'map'}>
                  <ActivityMap mapPolyline={mapPolyline} />
                </div>
              {/if}
            </div>
          </div>
        </div>
      {/if}

      <!-- Footer actions -->
      <div class="flex flex-row justify-between gap-3 pl-8 pr-5 py-4 border-t border-border bg-muted/30">
        <div class="flex-1"></div>
        <div class="flex gap-2 justify-end">
          <!-- TODO (hyde): remove console log -->
          <!-- <Button variant="default" on:click={() => console.log(exercise)}>Debug</Button> -->
          <Button variant="outline" href={detailPageLink} target={Activity.isWorkout(exercise) ? "workout" : "activity"}>
            More Details <i class="fa-solid fa-arrow-up-right-from-square text-xs ml-1" />
          </Button>
          <Button variant="outline" on:click={() => open = false}>Close</Button>
        </div>
      </div>
    </div>
  </Dialog.Content>
</Dialog.Root>
